import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Brain, CheckCircle, AlertTriangle, Clock, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AIBehaviorSettings() {
  const [rules, setRules] = useState("");
  const [saving, setSaving] = useState(false);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/system-behavior-rules`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || "");
      }
    } catch (e) { console.error("Failed to fetch rules:", e); }
  }, []);

  const fetchActorStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-behavior/status`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setActors(data.actors || []);
      }
    } catch (e) { console.error("Failed to fetch actor status:", e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRules();
    fetchActorStatus();
  }, [fetchRules, fetchActorStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/system-behavior-rules`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      if (res.ok) toast.success('System behavior rules saved');
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save rules'); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset system behavior rules to defaults?')) return;
    try {
      const res = await fetch(`${API_URL}/api/rag/cdm/system-behavior-rules/reset`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) { toast.success('Rules reset to defaults'); fetchRules(); }
      else toast.error('Failed to reset');
    } catch (e) { toast.error('Failed to reset'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffHours = Math.floor(diffMs / 3600000);
      if (diffHours < 1) return 'Just now';
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ai-behavior-settings">
      <div className="flex items-center gap-3">
        <Brain className="h-6 w-6 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Behavior</h1>
          <p className="text-sm text-gray-500">Configure global behavior rules that apply to ALL AI actors</p>
        </div>
      </div>

      {/* System Behavior Rules */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">System Behavior Rules</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Controls language enforcement, tone, and restrictions. Applied automatically to every AI actor on every message.
                Use <code className="bg-gray-100 px-1 rounded text-xs">{'{languages}'}</code> and <code className="bg-gray-100 px-1 rounded text-xs">{'{primary_language}'}</code> as placeholders — replaced with each actor's configured languages at runtime.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="text-xs h-8" onClick={handleReset}>Reset Defaults</Button>
              <Button size="sm" className="text-xs h-8" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save Rules
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <textarea
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 text-xs font-mono border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50"
            placeholder="Enter system behavior rules..."
          />
        </CardContent>
      </Card>

      {/* Applied To — Actor Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Applied To</CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                These rules are applied to all actors automatically. Status shows each actor's language config and last activity.
              </p>
            </div>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => { setLoading(true); fetchActorStatus(); }}>
              <RefreshCw className="h-3 w-3 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : actors.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No AI actors found</p>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 mb-3">
                {actors.length} actor{actors.length !== 1 ? 's' : ''} | {actors.filter(a => a.rules_applied).length} with languages configured
              </div>
              {actors.map((actor) => (
                <div key={actor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm shrink-0 overflow-hidden">
                      {actor.profile_image ? (
                        <img src={actor.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (actor.name || "?")[0]
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">{actor.name}</span>
                        {actor.actor_functions?.includes('support') && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs px-1 py-0">Support</Badge>
                        )}
                        {actor.actor_functions?.includes('booking') && (
                          <Badge className="bg-green-100 text-green-700 text-xs px-1 py-0">Booking</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{actor.language_display}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="h-3 w-3" />
                        {formatDate(actor.last_message_at)}
                      </div>
                    </div>
                    {actor.rules_applied ? (
                      <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Applied
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-600 text-xs flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> No languages
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
